import mori from 'mori';
import { getOneSheet } from './transducers';
import { appDefault } from '../config/config';
import slugify from '../util/slugify';

/**
 * Returns a map for nested columns
 *
 * @param {array} sheet - The flattened google document
 * @param {hashMap} config - Global config options parsed from the google document
 * @param {object} options - The current state ie router.currentParams
 *
 * @return {object} A nested map of parent column objects
 */
export default function(sheet, config, options) {
  const context = mori.getIn(config, ['globalRules', 'Context'], false);
  const label = context ? context[1] : 'States';

  const buildColumns = (data) =>
    mori.toJs(mori.into(
      mori.hashMap(),
      mori.map(
        (group) => {
          return mori.hashMap(
            mori.first(group),
            mori.first(group) === 'nested' ?
              mori.hashMap(
                slugify(label),
                mori.hashMap(
                  'children',
                  buildBaseColumns(
                    mori.first(mori.rest(group)),
                    options,
                    config
                  ),
                  'label', label
                )
              ) :
              buildBaseColumns(
                mori.first(mori.rest(group)),
                options,
                config
              )
            // create the base column selector for each row
          );
        },
        // nested vs wrapups
        mori.groupBy(
          (row) => isWrapup(row),
          mori.dropWhile((x) => !x[0], data)
        )
      )
    ));

  return {
    columns: buildColumns(getOneSheet(sheet)),
    columnsAll: sheet
    // Reduce to a map of {Shee_name: {headers}}
    .reduce((acc, curr) => ({
      [curr[0]]: { ...buildColumns(getOneSheet([curr])) },
      ...acc,
    }), {}),
  };
}

/**
 * Create base column selectors for each row. These looks a little different
 * depending on the nesting level of the indicator sheet.
 *
 * @example single-level-geographical data
 * { nested: { Alabama: { selected: bool } } }
 *
 * @example n-plus-level-geographical data
 * { nested: { Alabama: { children: { [Child]: { selected: bool } } } } }
 *
 * @example summary rows
 * { nested: { Alabama: { selected: bool, summary: { Alabama (All): {} } } } }
 */
function buildBaseColumns(items, options, config) {
  const isGeomulti = mori.getIn(config, ['globalRules', 'Geomulti'], false);

  // group by base column, normalizing summary rows
  const byState = mori.groupBy(
    (row) => (
      summaryItem(row, isGeomulti) ?
        mori.first(row).replace(appDefault.isSummaryRow, '') :
        mori.first(row)
    ),
    items
  );
  return mori.into(
    mori.hashMap(),
    mori.map(
      (row) => {
        if (!isGeomulti) {
          return mori.hashMap(
            slugify(mori.first(row)),
            mori.hashMap(
              'label',
              mori.first(row)
            )
          );
        }
        return mori.hashMap(
          slugify(mori.first(row)),
          mori.into(
            mori.hashMap(
              'label',
              mori.first(row)
            ),
            mori.map(
              (item) => buildNested(item),
              mori.groupBy(
                (item) => (
                  summaryItem(item, isGeomulti) ?
                    'summary' :
                    'children'
                ),
                mori.last(row)
              )
            )
          )
        );
      },
      byState
    )
  );
}

/**
 * Builds the tree of subitems in the column selector tree
 *
 * @param {vector} item The subgroup of rows for summary or children
 *
 * @return {hashMap} Node with summary and children collections
 */
function buildNested(item) {
  if (mori.first(item) === 'summary') {
    return mori.hashMap(
      'summary',
      mori.hashMap(
        'label',
        'Summary'
      )
    );
  }
  return mori.hashMap(
    'children',
    mori.into(
      mori.hashMap(),
      mori.map(
        (child) => mori.hashMap(
          slugify(mori.first(mori.rest(child))),
          mori.hashMap(
            'label',
            mori.first(mori.rest(child))
          )
        ),
        mori.last(item)
      )
    )
  );
}

/**
 * Test whether a row is a wrapup row
 *
 * @param {vector|array} x The current row
 *
 * @return {bool} isWrapup?
 */
function isWrapup(x) {
  return appDefault.specialWrapups.indexOf(mori.first(x)) > -1 ?
    'wrapups' :
    'nested';
}

/**
 * Test whether a row is a summary row
 *
 * @param {vector|array} x The current row
 * @param {bool} isGeomulti If we're in a geomulti indicator
 *
 * @return {bool} isSummaryItem?
 */
function summaryItem(x, isGeomulti) {
  return isGeomulti && appDefault.isSummaryRow.test(mori.first(x));
}



/** WEBPACK FOOTER **
 ** ../~/eslint-loader!./js/services/columnFilters.js
 **/